import { useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent, TouchEvent } from 'react';

type PressEvent = MouseEvent<HTMLElement> | TouchEvent<HTMLElement>;

interface UseLongPressOptions {
  /** Fired once the button has been held for `delay` ms. */
  onLongPress: () => void;
  /** Fired on a normal tap/click. */
  onShortPress: () => void;
  delay?: number;
}

/** Finger movement (px) that cancels a hold, so scrolling does not quote. */
const MOVE_TOLERANCE = 10;

/**
 * Distinguishes a tap from a hold on the same control.
 *
 * Why not just use onClick? A click always fires on release, so a long press
 * would trigger both actions. Here the timer decides which one runs.
 */
export function useLongPress({
  onLongPress,
  onShortPress,
  delay = 500,
}: UseLongPressOptions) {
  const timer = useRef<number | null>(null);
  const fired = useRef(false);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const lastTouchAt = useRef(0);
  const [pressing, setPressing] = useState(false);

  useEffect(() => {
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, []);

  /**
   * Mobile browsers replay a tap as mouse events after touchend. Ignore those
   * or a single tap would repost twice and a hold could repost as well.
   */
  const isSyntheticMouse = useCallback((event: PressEvent) => {
    if ('touches' in event) {
      lastTouchAt.current = Date.now();
      return false;
    }
    return Date.now() - lastTouchAt.current < 700;
  }, []);

  const cancel = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    fired.current = false;
    origin.current = null;
    setPressing(false);
  }, []);

  const start = useCallback(
    (event: PressEvent) => {
      if (isSyntheticMouse(event)) return;
      // Stop text selection on desktop. React's touchstart listener is passive,
      // so preventDefault there would only produce a console warning.
      if (event.type === 'mousedown') event.preventDefault();

      const touch = 'touches' in event ? event.touches[0] : null;
      origin.current = touch ? { x: touch.clientX, y: touch.clientY } : null;

      setPressing(true);
      fired.current = false;
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        timer.current = null;
        fired.current = true;
        origin.current = null;
        setPressing(false);
        onLongPress();
      }, delay);
    },
    [delay, isSyntheticMouse, onLongPress],
  );

  const end = useCallback(
    (event: PressEvent) => {
      if (isSyntheticMouse(event)) return;
      if (timer.current === null) {
        fired.current = false;
        origin.current = null;
        setPressing(false);
        return;
      }

      window.clearTimeout(timer.current);
      timer.current = null;
      origin.current = null;
      setPressing(false);

      if (!fired.current) {
        event.preventDefault();
        onShortPress();
      }
      fired.current = false;
    },
    [isSyntheticMouse, onShortPress],
  );

  /** Call from onTouchMove: a real swipe cancels the hold, jitter does not. */
  const move = useCallback(
    (event: PressEvent) => {
      if (timer.current === null || !origin.current) return;
      const touch = 'touches' in event ? event.touches[0] : null;
      if (!touch) return;
      const dx = touch.clientX - origin.current.x;
      const dy = touch.clientY - origin.current.y;
      if (Math.hypot(dx, dy) > MOVE_TOLERANCE) cancel();
    },
    [cancel],
  );

  return { pressing, start, end, cancel, move };
}
